"use client";

import { useRef, useEffect, useState } from "react";
import { useParallax } from "@/hooks/useParallax";
import "./style.scss";
import { BREAKPOINT } from "@/utils/constants";
import gsap from "gsap";
import AnimatedLogo from "./AnimatedLogo";

const Hero = () => {
	const logoRef = useRef<HTMLDivElement>(null);
	const headingRef = useRef<HTMLHeadingElement>(null);
	const typewriterRef = useRef<HTMLSpanElement>(null);
	const [isMobile, setIsMobile] = useState(false);

	// Detect if mobile on mount and resize
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < BREAKPOINT);
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);

		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	// Apply subtle parallax to logo - slow drift downward
	useParallax(logoRef, {
		speed: 0.7,
		direction: 'down',
		distance: 60,
		scrub: 1.5,
	});

	// Apply very subtle parallax to heading - slight upward movement
	useParallax(headingRef, {
		speed: 1.05,
		direction: 'up',
		distance: 30,
		scrub: 1.5,
	});

	// Typewriter animation for "We're Parallel Lab"
	useEffect(() => {
		const handlePreloaderExit = () => {
			if (!typewriterRef.current) return;

			const text = "We're Parallel Lab";
			const words = text.split(" ");

			// Clear the content initially
			typewriterRef.current.innerHTML = "";

			// Create spans for each word
			const wordElements = words.map(word => {
				const span = document.createElement("span");
				span.textContent = word;
				span.style.opacity = "0";
				span.style.display = "inline-block";
				span.style.marginRight = "0.3em";
				return span;
			});

			// Append all word spans to the container
			wordElements.forEach(span => typewriterRef.current?.appendChild(span));

			// Animate words in sequence (typewriter style)
			gsap.to(wordElements, {
				opacity: 1,
				duration: 0.3,
				stagger: 0.15,
				ease: "power2.out",
				delay: 1.5, // Delay after page reveal starts
			});
		};

		window.addEventListener("preloader-exiting", handlePreloaderExit);

		return () => {
			window.removeEventListener("preloader-exiting", handlePreloaderExit);
		};
	}, []);

	return (
		<section className="hero">
			<div className="hero__content">
				<h1 ref={headingRef}>
					<span ref={typewriterRef}>We're Parallel Lab</span>
					—a digital experience studio where fun meets function, and every pixel has purpose.
				</h1>
			</div>
			<div className="hero__img-container" ref={logoRef}>
				<AnimatedLogo isMobile={isMobile} />
			</div>
		</section>
	);
};

export default Hero;
